import { Link } from "react-router-dom";
import { FOOTER, INVESTMENTS } from "../mock";
import { Mail, Phone, MapPin, ShieldCheck } from "lucide-react";
import { useToast } from "../hooks/use-toast";
import { useState } from "react";

export default function Footer() {
  const props = INVESTMENTS.slice(0, 5);
  const more = INVESTMENTS.slice(5, 10);
  const { toast } = useToast();
  const [subscribing, setSubscribing] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    const email = new FormData(e.currentTarget).get("email");
    setSubscribing(true);
    try {
      const all = JSON.parse(localStorage.getItem("ihub_newsletter") || "[]");
      all.push({ email, at: new Date().toISOString() });
      localStorage.setItem("ihub_newsletter", JSON.stringify(all));
    } catch { /* ignore */ }
    await new Promise((r) => setTimeout(r, 400));
    toast({
      title: "You're subscribed",
      description: `We'll send the next monthly investor briefing to ${email}.`,
    });
    e.target.reset();
    setSubscribing(false);
  };

  return (
    <footer className="bg-[#0d1a2e] text-white relative overflow-hidden">
      <div className="absolute inset-0 bg-grid-dark opacity-40 pointer-events-none" />
      <div className="absolute -top-32 -right-32 w-[36rem] h-[36rem] glow-emerald-soft" />

      <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-20 pb-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8">
          <div className="lg:col-span-4">
            <div className="flex items-center gap-2.5">
              <div className="w-10 h-10 rounded-xl bg-[#0a1424] flex items-center justify-center border border-white/10">
                <ShieldCheck className="w-5 h-5 text-[#16d196]" strokeWidth={2.4} />
              </div>
              <div className="leading-tight">
                <div className="text-[19px] font-extrabold tracking-tight">
                  Investment<span className="text-[#16d196]">Hub</span>
                </div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 -mt-0.5 font-medium">
                  UK · No Cold Calls
                </div>
              </div>
            </div>
            <p className="mt-6 text-white/65 text-[14.5px] leading-relaxed max-w-md">
              Britain's trusted investment hub — covering property, bonds, ISAs,
              SIPPs, VCT/EIS and renewable infrastructure. Specialist, vetted, FCA-aware.
            </p>

            <div className="mt-8 space-y-3 text-[14px]">
              <a href={`mailto:${FOOTER.email}`} className="flex items-center gap-3 text-white/75 hover:text-white">
                <Mail className="w-4 h-4 text-[#16d196]" />
                <span>{FOOTER.email}</span>
              </a>
              <a href={`tel:${FOOTER.phone}`} className="flex items-center gap-3 text-white/75 hover:text-white">
                <Phone className="w-4 h-4 text-[#16d196]" />
                <span>{FOOTER.phone}</span>
              </a>
              <div className="flex items-start gap-3 text-white/75">
                <MapPin className="w-4 h-4 text-[#16d196] mt-0.5 shrink-0" />
                <span>{FOOTER.address}</span>
              </div>
            </div>

            <form onSubmit={subscribe} className="mt-8 max-w-sm">
              <label className="text-[11px] uppercase tracking-[0.18em] text-white/55 font-bold">
                Monthly investor briefing
              </label>
              <div className="mt-2 flex border border-white/15 hover:border-white/30 focus-within:border-[#16d196] rounded-full overflow-hidden bg-white/[0.04]">
                <input
                  name="email"
                  type="email"
                  required
                  placeholder="you@email.com"
                  className="flex-1 bg-transparent px-4 py-2.5 text-[13.5px] text-white placeholder-white/40 focus:outline-none"
                />
                <button
                  type="submit"
                  disabled={subscribing}
                  className="px-4 bg-[#16d196] hover:bg-[#13b884] text-[#0d1a2e] text-[13px] font-bold transition-colors disabled:opacity-60"
                >
                  {subscribing ? "…" : "Subscribe"}
                </button>
              </div>
            </form>
          </div>

          <div className="lg:col-span-3">
            <div className="text-[11px] uppercase tracking-[0.18em] text-white/45 mb-4 font-semibold">
              Investments
            </div>
            <ul className="space-y-2.5 text-[14px]">
              {props.map((p) => (
                <li key={p.slug}>
                  <Link to={`/investments/${p.slug}`} className="text-white/75 hover:text-white">
                    {p.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-3">
            <div className="text-[11px] uppercase tracking-[0.18em] text-white/45 mb-4 font-semibold">
              More offers
            </div>
            <ul className="space-y-2.5 text-[14px]">
              {more.map((p) => (
                <li key={p.slug}>
                  <Link to={`/investments/${p.slug}`} className="text-white/75 hover:text-white">
                    {p.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-2">
            <div className="text-[11px] uppercase tracking-[0.18em] text-white/45 mb-4 font-semibold">
              Company
            </div>
            <ul className="space-y-2.5 text-[14px]">
              <li><Link to="/about" className="text-white/75 hover:text-white">About us</Link></li>
              <li><Link to="/#how-it-works" className="text-white/75 hover:text-white">How it works</Link></li>
              <li><Link to="/#faq" className="text-white/75 hover:text-white">FAQ</Link></li>
              <li><Link to="/apply" className="text-white/75 hover:text-white">Get matched</Link></li>
              <li><Link to="/admin" className="text-white/45 hover:text-white">Admin</Link></li>
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-8 border-t border-white/10">
          <p className="text-[12px] text-white/45 leading-relaxed max-w-4xl">
            {FOOTER.disclaimer}
          </p>
          <div className="mt-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-[12px] text-white/55">
            <div>
              © {new Date().getFullYear()} {FOOTER.company} · Company No. {FOOTER.companyNo} · ICO {FOOTER.ico}
            </div>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
              {["Privacy", "Terms", "Cookies", "Risk warning"].map((label) => (
                <button
                  key={label}
                  type="button"
                  onClick={() =>
                    toast({
                      title: `${label} policy`,
                      description: "Document available on request — please contact our team.",
                    })
                  }
                  className="hover:text-white text-left"
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
